import type CadenceStepOption from '@poseidon/types/pg-gen/cadenceStepOption';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import type { CadenceGraph, CadenceGraphNode } from '@poseidon/types/svr/svrCadenceTemplate.type';
import type { TreeStep } from '@poseidon/types/types/treeStep.type';
import { MANUALLY_RESOLVABLE_STEP_STATES } from '@poseidon/types/types/stepResolution.type';

const stepTypeToOrder: { [K in CadenceStepType]: number } = {
  visit: 1,
  follow: 2,
  reminder: 3,
  call: 5,
  email: 6,
  likeRecentPosts: 8,
  connect: 9,
  inmail: 10,
  message: 11,
  delay: 12,
  wait_for_connect: 13,
  voice_note: 14,
  sift: 15,
  switch_campaign: 16,
  switch: 17,
  video_note: 18,
};

export function cadenceStepBuildGraph<
  S extends {
    id: string;
    parent_id: string | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): CadenceGraph<S> | null {
  let startId: string | undefined;

  const filteredSteps = steps.filter((s) => s.archived == false || s.archived === undefined);

  const nodes: Record<string, CadenceGraphNode<S>> = {};
  filteredSteps
    .map((s) => Object.assign({}, s, { next: {}, group: [] }))
    .forEach((s) => {
      nodes[s.id] = s;
    });

  // Tail of each wait_for_connect branch chain, keyed by
  // `${wfcId}:${option}`. A wait_for_connect can have MULTIPLE sibling
  // children under the same option (e.g. several No-branch steps, all
  // parent=wfc, option='two') — but `next` has a single slot per
  // option, so writing `next[option]` for each sibling would overwrite
  // all but the last, orphaning the earlier steps. Instead we chain
  // them: the first child of a branch goes into `next[option]`, and each
  // subsequent sibling is linked onto the previous child via `next.one`.
  // Consumers (`walkChainFrom`, the campaign StepNodeRenderer) already
  // walk that `next.one` chain, so this makes every sibling render.
  // Steps arrive in `created_on` order, so the chain follows insertion
  // order — the sequence the user built.
  const wfcBranchTail: Record<string, string> = {};
  // Same chaining bookkeeping for a `switch` node's option arms (one arm per band).
  const switchBranchTail: Record<string, string> = {};

  filteredSteps.forEach((step) => {
    if (step.parent_id == null) {
      startId = step.id;
      return;
    }

    const parentNode = nodes[step.parent_id];
    if (parentNode?.type == 'delay') {
      // `switch` is a BRANCH node (like wait_for_connect): it sits at the delay's next.one and
      // fans into option arms, so route it into `next`, not the runnable-content `group`.
      if (step.type == 'delay' || step.type == 'wait_for_connect' || step.type == 'switch') {
        parentNode.next[step.option] = step.id;
      } else {
        parentNode.group.push(step.id);
      }
    }
    if (parentNode?.type === 'wait_for_connect') {
      const key = `${parentNode.id}:${step.option}`;
      const tailId = wfcBranchTail[key];
      if (tailId == null) {
        parentNode.next[step.option] = step.id; // first leaf of this branch
      } else {
        nodes[tailId]!.next.one = step.id; // chain onto the previous sibling
      }
      wfcBranchTail[key] = step.id;
    }
    // A `switch` node's children are its arm heads (one delay block per band `option`), chained
    // exactly like wait_for_connect so a multi-step arm renders every sibling.
    if (parentNode?.type === 'switch') {
      const key = `${parentNode.id}:${step.option}`;
      const tailId = switchBranchTail[key];
      if (tailId == null) {
        parentNode.next[step.option] = step.id;
      } else {
        nodes[tailId]!.next.one = step.id;
      }
      switchBranchTail[key] = step.id;
    }
  });

  if (startId == null) return null;

  return {
    nodes,
    startId,
  };
}

export function cadenceStepBuildTree<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): TreeStep<S> | null {
  const rootNode = steps.find((s) => s.parent_id == null && (s.archived == false || s.archived === undefined));
  if (rootNode == null) return null;

  const childrenMap = new Map<ID, S[]>();
  steps.forEach((s) => {
    if (s.parent_id == null) return;

    let children = childrenMap.get(s.parent_id);
    if (children == null) {
      children = [];
      childrenMap.set(s.parent_id, children);
    }
    children.push(s);
  });

  // sort the children so the order by steps is the same
  childrenMap.forEach((value) => {
    value.sort((a, b) => {
      const orderA = stepTypeToOrder[a.type];
      const orderB = stepTypeToOrder[b.type];

      return orderA - orderB;
    });
  });

  const tree = _cadenceStepBuildHelper(rootNode, childrenMap);
  return tree;
}
function _cadenceStepBuildHelper<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(rootNode: S, childrenMap: Map<ID, S[]>): TreeStep<S> {
  const childrenNodes = childrenMap.get(rootNode.id);
  if (childrenNodes == null) {
    const treeNode: TreeStep<S> = { ...rootNode, children: [] };
    return treeNode;
  }

  const children = childrenNodes.map((n) => _cadenceStepBuildHelper(n, childrenMap));
  const nonArchived = children.filter((n) => n.archived !== true);
  const archived = children.filter((n) => n.archived === true);
  const treeNode: TreeStep<S> = { ...rootNode, children: nonArchived, archivedChildren: archived };
  return treeNode;
}

export function cadenceStepLeftListFromRawSteps<
  ID extends string | number,
  S extends {
    id: ID;
    parent_id: ID | null;
    option: CadenceStepOption;
    type: CadenceStepType;
    archived?: boolean | null;
  },
>(steps: S[]): (S & { triggerStep?: S })[] {
  // if the steps all have the same parent and there are no delay steps
  // these are runnable steps, just return them
  const parentIds = Array.from(new Set(steps.map((s) => s.parent_id)));
  const delayStep = steps.find((s) => s.type == 'delay');

  if (parentIds.length == 1 && delayStep == null) {
    return steps;
  }

  const tree = cadenceStepBuildTree(steps);
  if (tree == null) return [];

  const leftList = cadenceStepTreeBuildLeftList(tree);
  return leftList;
}

export function cadenceStepTreeBuildLeftList<
  ID extends string | number,
  S extends { id: ID; parent_id: ID | null; option: CadenceStepOption; type: CadenceStepType },
>(tree: TreeStep<S>): (S & { triggerStep?: S })[] {
  const acc: (S & { triggerStep?: S })[] = [tree];

  _cadenceStepTreeBuildLeftListHelper(tree, acc);
  return acc;
}

export function _cadenceStepTreeBuildLeftListHelper<
  ID extends string | number,
  S extends { id: ID; parent_id: ID | null; option: CadenceStepOption; type: CadenceStepType },
>(tree: TreeStep<S>, acc: (S & { triggerStep?: S })[]) {
  const rightChild = tree.children.find((c) => c.option == 'two');
  if (rightChild != null) {
    // there is a right child, attach to previous as trigger step
    const accLen = acc.length;
    const lastAccVal = acc[accLen - 1];
    if (lastAccVal) {
      lastAccVal.triggerStep = { ...rightChild };
    }
  }

  for (const child of tree.children) {
    if (child.option != 'one') continue;
    // switch is a branch node, not a runnable step — keep it out of the linear step list.
    if (child.type == 'wait_for_connect' || child.type == 'delay' || child.type == 'switch') continue;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, archivedChildren, ...childCopy } = child;
    acc.push(childCopy as unknown as S);
  }

  const next = tree.children.find((c) => c.type == 'delay' || c.type == 'wait_for_connect');

  if (next != null) {
    acc.push(next);
    _cadenceStepTreeBuildLeftListHelper(next, acc);
  }
}

/**
 * The minimum a campaign graph node exposes for the email-thread walk. Structural so the template
 * graph, the per-contact cadence graph, and the server's raw `cadence_step` rows all fit: they share
 * one tree shape, because a cadence is instantiated 1:1 from its template's steps (`parent_id`
 * included).
 */
export type EmailThreadGraphNode = {
  id: string;
  type: string;
  parent_id?: string | null;
  subject?: string | null;
};

/**
 * The email a step at `nodeId` continues, or null when nothing earlier is an email. The one
 * definition shared by the step editors (which hide a thread-continuing step's Subject field) and
 * the server (which then must not flag that hidden subject) — so the two can't disagree.
 *
 * Mirrors how the server threads a template's subjects into a contact's cadence
 * (`_cadenceStepCopyFromTemplateStepsTreeHelper`'s `inheritedEmailSubject`): sequential steps aren't
 * a parent→child chain — a day's runnable steps and the continuation-delay leading to the next day
 * are SIBLINGS under that day's delay. So:
 * - an EMAIL inherits what its day inherited — never a same-day email, so two emails on one day
 *   both continue the previous day's email, not each other;
 * - anything else (the next day's delay, a branch) inherits the email of the day it hangs off.
 *
 * One deliberate difference: a branch node (`wait_for_connect`, `switch`) fans out into mutually
 * exclusive arms (and chained leaves), so an email in one arm is never "earlier" than a step in
 * another — the walk looks past them to whatever ran before the branch. (The server's copy picks a
 * branch's first email child, but that arm never sends before this one, so it can't be the thread.)
 */
export function findPreviousEmailStep<N extends EmailThreadGraphNode>(
  nodes: Readonly<Record<string, N>>,
  nodeId: string,
): N | null {
  const seen = new Set<string>();
  let id: string | null = nodeId;
  while (id != null && !seen.has(id)) {
    seen.add(id);
    const node: N | undefined = nodes[id];
    const parent: N | undefined = node?.parent_id != null ? nodes[node.parent_id] : undefined;
    if (node == null || parent == null) return null;
    // An email inherits its day's inherited email — keep climbing from the parent.
    if (node.type === 'email') {
      id = parent.id;
      continue;
    }
    // Anything else inherits the email of the day it hangs off: an email parent directly (an
    // email → delay chain), or the email in the parent delay's own group.
    if (parent.type === 'email') return parent;
    if (parent.type === 'delay') {
      const dayEmail = findFirstEmailChild(nodes, parent.id);
      // (A step is never its own previous email — only reachable through a corrupt parent cycle.)
      if (dayEmail != null && dayEmail.id !== nodeId) return dayEmail;
    }
    // …else (an email-less day, or a branch node) whatever the parent itself inherited.
    id = parent.id;
  }
  return null;
}

/** The first email step directly under `parentId` — the day's email, as the server picks it. */
export function findFirstEmailChild<N extends EmailThreadGraphNode>(
  nodes: Readonly<Record<string, N>>,
  parentId: string,
): N | undefined {
  return Object.values(nodes).find((n) => n.parent_id === parentId && n.type === 'email');
}

/**
 * A subject that carries no text is no thread subject to inherit. Stored subjects are editor HTML,
 * so an empty editor's `<p></p>` counts as blank — the same tag-strip the editor validates with.
 */
export function blankSubjectToNull(subject: string | null | undefined): string | null {
  return subject != null && subject.replace(/<[^>]*>/g, '').trim().length > 0 ? subject : null;
}

const STILL_RUNS: ReadonlySet<string> = new Set(MANUALLY_RESOLVABLE_STEP_STATES);

/** Whether the engine will still run a step in this state (so an earlier email in it becomes the thread). */
export function stepStillRuns(state: string | null | undefined): boolean {
  return state != null && STILL_RUNS.has(state);
}

/** A per-contact cadence node: the thread walk's fields plus the step's run state. */
export type CadenceThreadGraphNode = EmailThreadGraphNode & { state?: string | null };

/**
 * The subject a per-contact (cadence) email step will inherit by continuing the thread, given the
 * earlier campaign email ({@link findPreviousEmailStep}) and the cadence owner's latest real email
 * with the contact — or null when the step has no thread to join and keeps its OWN subject.
 *
 * A cadence is live, so this mirrors what the send actually does (`cadenceStepExec`): a
 * thread-continuing step replies into the cadence owner's LATEST email with the contact — whatever
 * sent it, this campaign or not — and reuses that email's subject. So:
 * 1. An earlier email step in this campaign that the engine will still run becomes that latest email
 *    by the time this step sends → its subject.
 * 2. Otherwise the owner's latest real email with the contact → its subject. A subject-less thread
 *    yields null: the step keeps its own Subject, and the send still threads on the provider ids.
 * 3. Otherwise there's nothing to thread onto → null.
 *
 * Non-null ⇔ the step editor hides the step's Subject field — so it's also exactly when the server
 * must not flag that (hidden, never-sent) subject as an unresolved variable.
 */
export function resolveCadenceThreadSubject(
  previousEmailStep: CadenceThreadGraphNode | null,
  emailThread: { subject: string | null } | null,
): string | null {
  if (previousEmailStep != null && stepStillRuns(previousEmailStep.state)) {
    return blankSubjectToNull(previousEmailStep.subject);
  }
  return emailThread?.subject ?? null;
}
